import {
  ChoiceGroup,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Dropdown,
  IconButton,
  Label,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Stack,
  TextField,
  Toggle,
} from '@fluentui/react';
import { Patient } from '../../api';
import { PatientPreferredDoctor } from '../../api/patients.types';
import { MEDICARE_PAYER_ID, PCP_CONSENT_TYPE, PCP_DECLINE_TYPE, PCP_ENABLED, PCP_PAYER_TYPE, PCP_TYPE } from '../../config/constants';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AsyncData } from '../../redux/core';
import { toast } from '../../redux/message/actions';
import { CreateFormRequest } from '../../redux/patient-files/types';
import { PCPFormRequest, PCPStatusChangeRequest, PreferredDoctorRequest } from '../../redux/providers/types';
import { HealAPI } from '../../healApi';
import { PreferredDoctorSelector } from '../core/PreferredDoctorSelector';

interface Props {
  label: string;
  patient: Patient;
  preferredDoctor: AsyncData<PatientPreferredDoctor>;
  onSave: (patientId: string) => void;
}

export const EditPreferredDoctor = ({ label, patient, preferredDoctor, onSave }: Props) => {
  const dispatch = useDispatch();
  const [showDialog, setShowDialog] = useState(false);

  // Form variables
  const [preferredDoctorId, setPreferredDoctorId] = useState(preferredDoctor.data?.doctor.providerId || '');
  const [pcp, setPcp] = useState(false);
  const [consented, setConsented] = useState(false);
  const [selectedConsent, setSelectedConsent] = useState('TELEPHONE_OUTREACH');
  const [payerAttributed, setPayerAttributed] = useState(false);
  const [declineStatus, setDeclineStatus] = useState('DECLINED_BY_PATIENT');
  const [declineNotes, setDeclineNotes] = useState('');
  const [pcpFormData, setPCPFormData] = useState({ payerId: '', version: '', vanity: '' });

  const payerIsMedicare = pcpFormData.payerId === MEDICARE_PAYER_ID;
  const setPCPFields = useCallback(() => {
    const { isFetching, data } = preferredDoctor;
    if (!isFetching && data) {
      setPreferredDoctorId(data.doctor.providerId);
      setPcp(PCP_ENABLED.some((status) => status === data.status));
      setConsented(PCP_CONSENT_TYPE.some((status) => status.key === data.consentType));
      setSelectedConsent(data.consentType || 'TELEPHONE_OUTREACH');
      setPayerAttributed(data.status === 'ATTRIBUTED_BY_PAYER');
      setDeclineStatus('DECLINED_BY_PATIENT');
    }
  }, [preferredDoctor]);

  useEffect(() => {
    setPCPFields();
  }, [preferredDoctor, setPCPFields]);

  const openDialog = useCallback(() => {
    HealAPI.fetchPCPFormData(patient.patientId).then((response) => {
      if (response.ok && response.data) {
        setPCPFormData(response.data.data);
        setShowDialog(true);
      } else {
        dispatch(
          toast({
            type: 'error',
            title: 'Error',
            content: response.data?.description || 'Could not fetch PCP form information. ',
          })
        );
      }
    });
  }, [patient, dispatch]);

  const cancelAndClose = () => {
    setPCPFields();
    setShowDialog(false);
  };

  const onSaveClicked = () => {
    const pcpStatus: string = payerAttributed ? 'ATTRIBUTED_BY_PAYER' : 'ATTRIBUTED_BY_HEAL';
    if (
      (pcp === true && !PCP_ENABLED.some((status) => status === preferredDoctor.data?.status)) ||
      (pcp === true && preferredDoctorId !== preferredDoctor.data?.doctor.providerId)
    ) {
      // Turning PCP ON, or changing preferred providers with PCP ON
      enablePCP(pcpStatus);
    } else if (pcp === false && PCP_ENABLED.some((status) => status === preferredDoctor.data?.status)) {
      // Turning PCP OFF
      disablePCP();
    } else if (pcp === false && !PCP_ENABLED.some((status) => status === preferredDoctor.data?.status)) {
      // Switching preferred providers, PCP OFF
      updatePreferredProviders();
    } else if ((pcp === true && selectedConsent !== preferredDoctor.data?.consentType) || (pcp === true && pcpStatus !== preferredDoctor.data?.status)) {
      // Update consent type or Attributed status, PCP ON
      updatePreferredProviders(pcpStatus, selectedConsent);
    }
  };

  const updatePreferredProviders = (pcpStatus?: string, consentType?: string) => {
    const request: Partial<PreferredDoctorRequest> = {
      providerId: preferredDoctorId,
    };

    if (pcpStatus) request.status = pcpStatus;
    if (consentType) request.pcpConsentType = consentType;

    HealAPI.setPatientPreferredDoctor(patient.patientId, request).then((response) => {
      if (response.ok) {
        setShowDialog(false);
        onSave(patient.patientId);
      } else {
        dispatch(
          toast({
            type: 'error',
            title: 'Error',
            content: 'Preferred provider could not be updated',
          })
        );
      }
    });
  };

  /**
   * Disables PCP for a patient
   * 1. Updates the PCP status for the patient
   * 2. Update the preferred provider, if changed
   */
  const disablePCP = async () => {
    const disableRequest: Partial<PCPStatusChangeRequest> = {
      providerId: preferredDoctor.data?.doctor.providerId,
      status: declineStatus,
      consentType: null,
      declineReason: 'OTHER',
      declineNotes: declineNotes,
    };
    try {
      const statusResponse = await HealAPI.setPCPStatus(patient.patientId, disableRequest);
      if (!statusResponse.ok) throw Error('PCP status could not be updated');

      if (preferredDoctorId !== preferredDoctor.data?.doctor.providerId) {
        const newProviderRequest: Partial<PreferredDoctorRequest> = {
          providerId: preferredDoctorId,
        };

        const providerResponse = await HealAPI.setPatientPreferredDoctor(patient.patientId, newProviderRequest);
        if (!providerResponse.ok) throw Error('Preferred provider could not be updated');
      }
      setShowDialog(false);
      onSave(patient.patientId);
    } catch (e: any) {
      dispatch(
        toast({
          type: 'error',
          title: 'Error',
          content: e,
        })
      );
    }
  };

  /**
   * Enable PCP for a patient.
   * 1. Fetches PCP form data for the patient.
   * 2. Uses the PCP form data to allocate a PCP form on filemanager
   * 3. Generate and upload a PCP file to filemanager
   * 4. Update the preferred provider for the patient
   */
  const enablePCP = async (pcpStatus: string) => {
    try {
      const formRequest: CreateFormRequest = {
        type: 'pcp',
        provider_id: preferredDoctorId,
        payer_id: pcpFormData.payerId,
        revision: pcpFormData.version,
        vanity: pcpFormData.vanity,
      };

      // Set the preferred provider
      const preferredDoctorRequest: Partial<PreferredDoctorRequest> = {
        providerId: preferredDoctorId,
        status: pcpStatus,
        pcpConsentType: consented ? selectedConsent : null,
      };

      const preferredDoctorResponse = await HealAPI.setPatientPreferredDoctor(patient.patientId, preferredDoctorRequest);
      if (!preferredDoctorResponse.ok) throw Error('Preferred provider could not be updated');

      // Create PCP form in filemanager
      const form = await HealAPI.createForm(patient.patientId, formRequest);

      if (!form.ok && form.data?.description && form.data.description.indexOf('Form with matching metadata already exists') > -1) {
        // Duplicate, ignore.
      } else if (!form.ok || !form.data?._items) {
        throw Error('Could not create PCP form');
      } else {
        const createPCPFormRequest: PCPFormRequest = {
          signedUrl: form.data._items[0].upload_url.put_url,
        };

        // Generate and upload a PCP form to presigned URL
        const createForm = await HealAPI.generateAndUploadPCPForm(patient.patientId, preferredDoctorId, createPCPFormRequest);
        if (!createForm.ok || !createForm.data) throw Error('Could not generate/upload PCP form');
      }

      setShowDialog(false);
      onSave(patient.patientId);
    } catch (e: any) {
      dispatch(
        toast({
          type: 'error',
          title: 'Error',
          content: e.message,
        })
      );
    }
  };

  const disableSave = (): boolean => {
    if (pcp && payerIsMedicare) {
      // Original Medicare cannot be set over the phone.
      return true;
    }

    if (pcp && !consented) {
      // Must provide consent if PCP is enabled
      return true;
    }

    return false;
  };

  const renderDialog = (): JSX.Element | null => {
    if (!showDialog) {
      return null;
    }

    return (
      <Dialog
        hidden={false}
        onDismiss={cancelAndClose}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Preferred Provider',
        }}
        modalProps={{
          isBlocking: true,
        }}
        minWidth={600}
      >
        <Stack tokens={{ childrenGap: 10 }}>
          <Stack.Item>
            <Stack horizontal disableShrink tokens={{ childrenGap: 10 }}>
              <PreferredDoctorSelector
                patientId={patient.patientId}
                doctor={{
                  prefix: preferredDoctor.data?.doctor.prefix,
                  firstName: preferredDoctor.data?.doctor.firstName,
                  lastName: preferredDoctor.data?.doctor.lastName,
                  avatarUrl: preferredDoctor.data?.doctor.avatarUrl,
                  providerId: preferredDoctorId,
                }}
                onChange={(id) => setPreferredDoctorId(id)}
              />
              <Toggle checked={pcp} inlineLabel label="Set as PCP" onChange={(ev, checked) => setPcp(checked!)} disabled={preferredDoctorId === ''} />
            </Stack>
          </Stack.Item>

          {pcp && payerIsMedicare && (
            <Stack.Item>
              <MessageBar messageBarType={MessageBarType.error}>Patient is on Medicare. Setting the primary care provider must happen in person with the care provider.</MessageBar>
            </Stack.Item>
          )}

          {pcp && (
            <>
              <Stack.Item>
                <p style={{ color: 'blue' }}>
                  {PCP_PAYER_TYPE.some((payer) => pcpFormData.vanity.startsWith(payer.key)) && 'PCP selection/change form will be sent to payer. Please get patient consent.'}
                </p>
                <Label>PCP Patient Consent</Label>
                <p>
                  In order to have Heal Doctors as your Primary Care Provider, we will need to generate a form on your behalf which will also be sent to your health plan. This
                  allows your Heal Doctors provider to serve as the PCP to guide you towards important preventive care to keep you healthy. Depending on your health plan, you may
                  receive a new member ID card in the mail, though this is not required to see a Heal Doctors provider immediately.
                </p>
              </Stack.Item>
              <Stack.Item>
                <Label>Do you consent Heal Doctors to generate a pre-populated &quot;PCP Change Form&quot; on your behalf?</Label>
                <Toggle defaultChecked={consented} inlineLabel onText="Yes" offText="No" onChange={(ev, checked) => setConsented(checked!)} />
              </Stack.Item>
              {consented && (
                <Dropdown
                  label="Patient Consent Type"
                  selectedKey={selectedConsent}
                  options={PCP_CONSENT_TYPE}
                  onChange={(ev, option) => setSelectedConsent(option!.key as string)}
                />
              )}
              <Stack horizontal horizontalAlign="space-between">
                <Stack.Item>
                  <Label>Attribution Status:</Label>
                  {PCP_TYPE.filter((status) => status.key === preferredDoctor.data?.status)[0]?.text}
                </Stack.Item>
                <Stack.Item>
                  <Label>{PCP_PAYER_TYPE.filter((payer) => pcpFormData.vanity.startsWith(payer.key))[0]?.text || 'Attributed by payer'}</Label>
                  <Toggle
                    defaultChecked={payerAttributed}
                    disabled={!PCP_PAYER_TYPE.some((payer) => pcpFormData.vanity.startsWith(payer.key))}
                    inlineLabel
                    onText="Yes"
                    offText="No"
                    onChange={(ev, checked) => setPayerAttributed(checked!)}
                  />
                </Stack.Item>
              </Stack>
            </>
          )}

          {!pcp && PCP_ENABLED.some((status) => status === preferredDoctor.data?.status) && (
            <>
              <ChoiceGroup
                label="Reason for declining PCP"
                options={PCP_DECLINE_TYPE}
                required={true}
                defaultSelectedKey="DECLINED_BY_PATIENT"
                onChange={(ev, o) => setDeclineStatus(o!.key)}
              />
              <TextField multiline={true} rows={3} required={false} placeholder="Notes" onChange={(ev, v) => setDeclineNotes(v!)} />
            </>
          )}
        </Stack>
        <DialogFooter>
          <PrimaryButton text="Save" onClick={onSaveClicked} disabled={disableSave()} />
          <DefaultButton text="Cancel" onClick={cancelAndClose} />
        </DialogFooter>
      </Dialog>
    );
  };

  return (
    <div className="flex">
      {label && <Label>{label}&nbsp;</Label>}
      <IconButton iconProps={{ iconName: 'Edit' }} onClick={openDialog} />
      {renderDialog()}
    </div>
  );
};
